import { defaultRangeExtractor, useVirtualizer } from "@tanstack/react-virtual";
import { useLayoutEffect, useRef, useState, type MutableRefObject, type ReactNode, type RefObject } from "react";

/** Variable-height chat rows. Focused controls remain mounted while scrolling. */
export function VirtualTranscript<T extends { id: number | string }>({
  items, parentRef, followLatest, renderItem,
}: {
  items: T[];
  parentRef: RefObject<HTMLDivElement>;
  followLatest: MutableRefObject<boolean>;
  renderItem: (item: T, index: number) => ReactNode;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [margin, setMargin] = useState(0);
  const [focusedId, setFocusedId] = useState<number | string | null>(null);
  const frame = useRef<number | null>(null);
  const focusedIndex = items.findIndex((item) => item.id === focusedId);
  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    getItemKey: (index) => items[index]!.id,
    estimateSize: () => 180,
    overscan: 6,
    scrollMargin: margin,
    rangeExtractor: (range) => {
      const indices = defaultRangeExtractor(range);
      if (focusedIndex >= 0 && !indices.includes(focusedIndex)) indices.push(focusedIndex);
      return indices.sort((a, b) => a - b);
    },
    onChange: () => {
      if (!followLatest.current || frame.current !== null) return;
      frame.current = requestAnimationFrame(() => {
        frame.current = null;
        if (followLatest.current && parentRef.current) {
          parentRef.current.scrollTop = parentRef.current.scrollHeight;
        }
      });
    },
  });

  useLayoutEffect(() => {
    const parent = parentRef.current;
    const container = containerRef.current;
    if (!parent || !container) return;
    const measure = () => setMargin(container.getBoundingClientRect().top - parent.getBoundingClientRect().top + parent.scrollTop);
    measure();
    if (followLatest.current && frame.current === null) {
      frame.current = requestAnimationFrame(() => {
        frame.current = null;
        if (followLatest.current) parent.scrollTop = parent.scrollHeight;
      });
    }
    const observer = new ResizeObserver(measure);
    observer.observe(parent);
    if (container.parentElement) observer.observe(container.parentElement);
    return () => {
      observer.disconnect();
      if (frame.current !== null) cancelAnimationFrame(frame.current);
      frame.current = null;
    };
  }, [parentRef, followLatest]);

  return (
    <div ref={containerRef} role="list" aria-label="Сообщения диалога"
      style={{ height: virtualizer.getTotalSize(), position: "relative", overflowAnchor: "none" }}>
      {virtualizer.getVirtualItems().map((row) => (
        <div key={row.key} data-index={row.index} data-message-id={String(items[row.index]!.id)}
          ref={virtualizer.measureElement} role="listitem" aria-posinset={row.index + 1} aria-setsize={items.length}
          onFocusCapture={() => setFocusedId(items[row.index]!.id)}
          onBlurCapture={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocusedId(null);
          }}
          style={{ position: "absolute", top: 0, left: 0, width: "100%", transform: `translateY(${row.start - margin}px)`, paddingBottom: "var(--layout-gap)" }}>
          {renderItem(items[row.index]!, row.index)}
        </div>
      ))}
    </div>
  );
}
